import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useUpload } from '../../hooks/useUpload.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  ADMIN_INPUT_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';

const inputClass = ADMIN_INPUT_CLASS;

const empty = {
  nombre: '',
  descripcion: '',
  imagen: '',
  telefono: '',
  portafolio_pdf: '',
  link_pagina: '',
  orden: 0,
  visible: true,
};

export default function EmprendimientosAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const { uploadFile, uploadDocument, uploading } = useUpload();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  const imagenPath = watch('imagen');
  const pdfPath = watch('portafolio_pdf');

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/emprendimientos', {
        params: { page, per_page: perPage, search: debouncedSearch },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  function openCreate() {
    setEditingId(null);
    reset(empty);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    reset({
      nombre: row.nombre ?? '',
      descripcion: row.descripcion ?? '',
      imagen: row.imagen ?? '',
      telefono: row.telefono ?? '',
      portafolio_pdf: row.portafolio_pdf ?? '',
      link_pagina: row.link_pagina ?? '',
      orden: Number(row.orden) || 0,
      visible: Number(row.eliminado) === 0,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      nombre: values.nombre.trim(),
      descripcion: values.descripcion?.trim() ?? '',
      imagen: values.imagen?.trim() ?? '',
      telefono: values.telefono?.trim() ?? '',
      portafolio_pdf: values.portafolio_pdf?.trim() ?? '',
      link_pagina: values.link_pagina?.trim() ?? '',
      orden: Number(values.orden) || 0,
      eliminado: values.visible ? 0 : 1,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/v1/emprendimientos/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/emprendimientos', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete(
      '¿Está seguro de eliminar este emprendimiento?',
      row.nombre || 'Esta acción no se puede deshacer.'
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/emprendimientos/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function onPickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file);
      setValue('imagen', path, { shouldValidate: true });
      toastSuccess('Imagen lista', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  async function onPickPdf(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadDocument(file);
      setValue('portafolio_pdf', path, { shouldValidate: true });
      toastSuccess('Portafolio listo', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  async function toggleVisible(row) {
    try {
      const { data } = await api.put(`/v1/emprendimientos/${row.id}`, {
        nombre: row.nombre,
        descripcion: row.descripcion ?? '',
        imagen: row.imagen ?? '',
        telefono: row.telefono ?? '',
        portafolio_pdf: row.portafolio_pdf ?? '',
        link_pagina: row.link_pagina ?? '',
        orden: Number(row.orden) || 0,
        eliminado: Number(row.eliminado) === 0 ? 1 : 0,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Visibilidad actualizada', '');
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  const columns = useMemo(
    () => [
      { key: 'orden', label: 'Orden' },
      { key: 'nombre', label: 'Nombre' },
      {
        key: 'telefono',
        label: 'Contacto',
        render: (row) => row.telefono?.trim() || '—',
      },
      {
        key: 'portafolio_pdf',
        label: 'PDF',
        render: (row) => (row.portafolio_pdf ? 'Sí' : '—'),
      },
      {
        key: 'eliminado',
        label: 'Portal',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleVisible(row)}
            className={adminPublishStatusClasses(Number(row.eliminado) === 0)}
          >
            {Number(row.eliminado) === 0 ? 'Visible' : 'Oculto'}
          </button>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Editar"
              onClick={() => openEdit(row)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_DELETE_BTN_CLASS}
              title="Eliminar"
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">
            Emprendimientos
          </h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Publica emprendimientos de asociados en /emprendimientos (imagen, descripción, contacto,
            portafolio PDF y enlace web).
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nuevo emprendimiento
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Editar emprendimiento' : 'Nuevo emprendimiento'}
        wide
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <FormField label="Nombre" error={errors.nombre?.message}>
            <input
              className={inputClass}
              {...register('nombre', { required: 'El nombre es obligatorio' })}
            />
          </FormField>
          <FormField
            label="Descripción (HTML permitido)"
            hint="Puedes usar párrafos, negritas, listas o enlaces. Evita scripts."
          >
            <textarea
              rows={8}
              className={`${inputClass} font-mono text-xs`}
              placeholder="<p>Descripción del emprendimiento…</p>"
              {...register('descripcion')}
            />
          </FormField>
          <FormField
            label="Imagen"
            hint={uploading ? 'Subiendo…' : 'Logo o foto del emprendimiento (opcional).'}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <input type="file" accept="image/*" onChange={onPickImage} disabled={uploading} />
              <input
                type="text"
                className={`${inputClass} flex-1 text-xs`}
                placeholder="/uploads/..."
                {...register('imagen')}
              />
            </div>
            {imagenPath ? (
              <img
                src={assetUrl(imagenPath)}
                alt=""
                className="mt-3 max-h-24 rounded-lg border border-white/10 object-contain"
              />
            ) : null}
          </FormField>
          <FormField label="Número de contacto" hint="Celular o fijo para llamada / WhatsApp.">
            <input
              type="tel"
              className={inputClass}
              placeholder="3001234567"
              {...register('telefono')}
            />
          </FormField>
          <FormField
            label="Portafolio (PDF)"
            hint={uploading ? 'Subiendo…' : 'Documento PDF opcional con productos o servicios.'}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={onPickPdf}
                disabled={uploading}
              />
              <input
                type="text"
                className={`${inputClass} flex-1 text-xs`}
                placeholder="/uploads/..."
                {...register('portafolio_pdf')}
              />
            </div>
            {pdfPath ? (
              <a
                href={assetUrl(pdfPath)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex text-sm text-accent-lime hover:text-white"
              >
                Ver PDF actual
              </a>
            ) : null}
          </FormField>
          <FormField label="Enlace de página web" hint="Opcional. Ej. https://miemprendimiento.com">
            <input
              type="url"
              className={inputClass}
              placeholder="https://..."
              {...register('link_pagina')}
            />
          </FormField>
          <FormField label="Orden en listado">
            <input type="number" className={inputClass} {...register('orden')} />
          </FormField>
          <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-300">
            <input type="checkbox" className="rounded border-white/20" {...register('visible')} />
            Visible en el portal público
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={uploading}>
              Guardar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa">
        {previewRow && (
          <div className="space-y-4 text-sm text-slate-300">
            {previewRow.imagen ? (
              <img
                src={assetUrl(previewRow.imagen)}
                alt=""
                className="max-h-32 rounded-lg border border-white/10 object-contain"
              />
            ) : null}
            <p>
              <span className="text-slate-500">Nombre:</span> {previewRow.nombre}
            </p>
            <div>
              <p className="mb-2 text-slate-500">Descripción:</p>
              {previewRow.descripcion ? (
                <div
                  className="max-w-none space-y-2 text-sm leading-relaxed text-slate-300 [&_a]:text-accent-lime [&_li]:ml-4 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: previewRow.descripcion }}
                />
              ) : (
                <p>—</p>
              )}
            </div>
            <p>
              <span className="text-slate-500">Contacto:</span> {previewRow.telefono || '—'}
            </p>
            <p>
              <span className="text-slate-500">Página:</span> {previewRow.link_pagina || '—'}
            </p>
            {previewRow.portafolio_pdf ? (
              <a
                href={assetUrl(previewRow.portafolio_pdf)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex text-accent-lime hover:text-white"
              >
                Abrir portafolio PDF →
              </a>
            ) : null}
          </div>
        )}
      </Modal>
    </div>
  );
}
